import React, { useMemo } from 'react';
import moment from 'moment';

import isObject from 'lodash/isObject';

import { StorageKeys } from '../constants/storageKeys';
import { withSeparators, bytesToSize } from '../internals/utils';

import useLocalStorage from './useLocalStorage';

import {
  sortWithNullOrUndefinedValues,
  sortBooleanWithNullOrUndefinedValues,
} from 'Internals/sort';
import { upstreamPipeIsGlobal, isMaintained, getDatasetQueueSize } from 'Internals/datasets';
import DatasetLink from 'Common/Links/DatasetLink';
import State from '../components/common/renderers/State/State';
import { getSortValues } from '../components/filter-table/FilterTable';

import type { Dataset } from '../types/dataset.types';
import type { FilterCell } from '../types/pipesList.types';
import type { PipeMap } from '../types/pipes.types';
import type { Upstreams } from '../types/state.types';
import type { SomeObject } from '../types/common.types';

export const useDatasetsListColumns = (
  pipes: PipeMap,
  upstreams: Upstreams,
  storageKey: string
) => {
  const [showBytesExact, setShowBytesExact] = useLocalStorage(
    StorageKeys.dataTable,
    [storageKey, 'showBytesExact'],
    true
  );

  const toggleShowBytesExact = () => {
    setShowBytesExact(!showBytesExact);
  };

  const getIndexCountNoDeleted = (dataset: Dataset) =>
    dataset.runtime?.['count-index-exists'] - dataset.runtime?.['count-index-deleted'];

  const getLastModified = (lastModified: string) => {
    if (lastModified === null) {
      return 'never';
    }

    return `${moment.duration(moment().diff(lastModified)).humanize()} ago`;
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Dataset',
        id: 'datasets-link',
        fixed: true,
        width: 300,
        accessor: (d: Dataset) => ({ id: d._id, link: d.link }),
        Cell: ({ cell: { value } }: FilterCell) => (
          <div>
            <DatasetLink datasetId={value.link}>{value.id}</DatasetLink>
          </div>
        ),
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          return sortWithNullOrUndefinedValues(a.id, b.id);
        }),
      },
      {
        Header: 'Last modified',
        id: 'datasets-last-modified',
        accessor: 'runtime[last-modified]',
        Cell: ({ cell: { value } }: FilterCell) => getLastModified(value),
        sortType: getSortValues((a: string, b: string) => {
          return sortWithNullOrUndefinedValues(new Date(a), new Date(b));
        }),
      },
      {
        Header: 'Index w/o deleted',
        id: 'datasets-index-without-deleted',
        accessor: (d: Dataset) => getIndexCountNoDeleted(d),
        align: 'right',
        Cell: ({ cell: { value } }: FilterCell) => <span>{withSeparators(value)}</span>,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Complete index',
        id: 'datasets-complete-index',
        accessor: 'runtime[count-index-exists]',
        align: 'right',
        Cell: ({ cell: { value } }: FilterCell) => <span>{withSeparators(value)}</span>,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Complete log',
        id: 'datasets-complete-log',
        accessor: 'runtime[count-log-exists]',
        align: 'right',
        Cell: ({ cell: { value } }: FilterCell) => <span>{withSeparators(value)}</span>,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Bytes used',
        id: 'pipes-bytes-used',
        accessor: (d: Dataset) => (isObject(d.storage) ? d.storage.total : d.storage),
        align: 'right',
        Cell: ({ cell: { value } }: FilterCell) => (
          <span
            onClick={() => toggleShowBytesExact()}
            title="Click to switch between representation"
            style={{ cursor: 'pointer' }}
          >
            {showBytesExact ? withSeparators(value) : bytesToSize(value)}
          </span>
        ),
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Origin',
        id: 'datasets-origin',
        accessor: 'runtime.origin',
        Cell: ({ cell: { value } }: FilterCell) => <span>{value}</span>,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Queues',
        id: 'datasets-queues',
        accessor: (d: Dataset) => d,
        align: 'right',
        Cell: ({ cell: { value } }: FilterCell) => (
          <div>{withSeparators(getDatasetQueueSize(value))}</div>
        ),
        sortType: getSortValues((a: Dataset, b: Dataset) => {
          const aQ = getDatasetQueueSize(a);
          const bQ = getDatasetQueueSize(b);

          return sortWithNullOrUndefinedValues(aQ, bQ);
        }),
      },
      {
        Header: 'Populated',
        id: 'datasets-populated',
        accessor: 'runtime.populated',
        align: 'center',
        width: 75,
        Cell: ({ cell: { value } }: FilterCell) =>
          value ? (
            <State title="Dataset is populated" className="status-green" text="Yes" />
          ) : (
            <State title="Dataset is not populated" className="status-red" text="No" />
          ),
        sortType: getSortValues(sortBooleanWithNullOrUndefinedValues),
      },
      {
        Header: 'Maintained',
        id: 'datasets-maintained',
        width: 75,
        accessor: (d: Dataset) => isMaintained(d._id, pipes, upstreams),
        align: 'center',
        Cell: ({ cell: { value } }: FilterCell) =>
          value ? (
            <State
              title="Dataset is maintained (has upstream pipe)"
              className="status-green"
              text="Yes"
            />
          ) : (
            <State
              title="Dataset is not maintained (has no upstream pipe)"
              className="status-red"
              text="No"
            />
          ),
        sortType: getSortValues(sortBooleanWithNullOrUndefinedValues),
      },
      {
        Header: 'Global',
        id: 'datasets-global',
        width: 75,
        accessor: (d: Dataset) => upstreamPipeIsGlobal(d._id, pipes, upstreams),
        align: 'center',
        Cell: ({ cell: { value } }: FilterCell) =>
          value ? (
            <State title="Upstream pipe is global" className="status-green" text="Yes" />
          ) : (
            <State title="Upstream pipe is not global" className="status-red" text="No" />
          ),
        sortType: getSortValues(sortBooleanWithNullOrUndefinedValues),
      },
    ];
  }, []);

  return columns;
};
